import { useEffect, useState } from "react";
import { Smartphone, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 30000;

function Stat({ label, value, tone, testid }) {
  return (
    <div data-testid={testid} className="min-w-[96px]">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#3a3d44]/50">{label}</div>
      <div className={`font-display font-extrabold text-[22px] leading-none mt-1.5 tabular-nums ${tone || "text-[#0B0D10]"}`}>{value}</div>
    </div>
  );
}

function ago(iso) {
  if (!iso) return "never";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
}

export function WalletHealthCard() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gcheck, setGcheck] = useState(null);
  const load = () => api.get("/admin/wallet/health").then((r) => setH(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, []);
  if (!h) return null;

  const d = h.last_24h || {};
  const attention = !h.apns_configured || h.overdue > 0 || d.gave_up > 0;
  const status = !h.apns_configured ? "APNs not configured" : h.overdue > 0 ? `${h.overdue} overdue` : d.gave_up > 0 ? `${d.gave_up} gave up` : "Healthy";
  const sweep = () => { setBusy(true); api.post("/admin/wallet/sweep").then(load).finally(() => setBusy(false)); };
  const checkGoogle = () => {
    setGcheck({ busy: true });
    api.post("/admin/wallet/google/check").then((r) => setGcheck(r.data)).catch((e) => setGcheck({ ok: false, message: e?.response?.data?.detail || "Check failed" }));
  };

  return (
    <div data-testid="wallet-health" className="rounded-2xl bg-white border border-[#0B0D10]/10 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex items-center gap-3 min-w-[200px]">
          <div className="h-9 w-9 rounded-xl bg-[#0B0D10] text-[#C8FF4D] grid place-items-center"><Smartphone size={16} strokeWidth={2.2} /></div>
          <div>
            <div className="text-[13.5px] font-semibold text-[#0B0D10]">Wallet live updates <span data-testid="wallet-health-build" className="ml-1.5 font-mono text-[10px] text-[#3a3d44]/45">{h.build}</span></div>
            <div data-testid="wallet-health-status" className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${attention ? "text-[#C2410C]" : "text-[#3a7d2c]"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${attention ? "bg-[#FF7A3D]" : "bg-[#5BBF3A]"}`} />{status}
            </div>
          </div>
        </div>
        <Stat label="Pending" value={h.pending} tone={h.overdue ? "text-[#C2410C]" : undefined} testid="wallet-health-pending" />
        <Stat label="Pushed 24h" value={d.delivered ?? 0} testid="wallet-health-delivered" />
        <Stat label="Phones woken" value={d.devices_notified ?? 0} testid="wallet-health-devices" />
        <Stat label="Passes refetched" value={d.fetched ?? 0} testid="wallet-health-fetched" />
        <Stat label="Retries" value={d.retries ?? 0} tone={d.retries ? "text-[#C2410C]" : undefined} testid="wallet-health-retries" />
        <Stat label="Awaiting phone" value={h.awaiting_phone ?? 0} tone={d.unverified ? "text-[#C2410C]" : undefined} testid="wallet-health-awaiting" />
        <Stat label="Held (Apple limit)" value={d.deferred ?? 0} testid="wallet-health-deferred" />
        <Stat label="Live devices" value={h.registrations_active} testid="wallet-health-registrations" />
        <div className="ml-auto flex items-center gap-3 text-[11.5px] text-[#3a3d44]/60">
          <span data-testid="wallet-health-last-push">Last phone push {ago(h.last_device_push_at)}</span>
          <button data-testid="wallet-health-sweep" onClick={sweep} disabled={busy}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#E4E0D6] bg-white text-[12px] font-medium text-[#0B0D10] hover:border-[#0B0D10]/40 disabled:opacity-50 transition-colors">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Retry now
          </button>
        </div>
      </div>
      {h.google_wallet && (
        <div data-testid="wallet-health-google" className="mt-4 pt-3 border-t border-[#0B0D10]/[0.06] flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="min-w-[200px]">
            <div className="text-[13px] font-semibold text-[#0B0D10]">Google Wallet</div>
            <div data-testid="wallet-health-google-status" className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${h.google_wallet.configured ? "text-[#3a7d2c]" : "text-[#3a3d44]/60"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${h.google_wallet.configured ? "bg-[#5BBF3A]" : "bg-[#3a3d44]/30"}`} />
              {h.google_wallet.configured ? "Connected · updates server-side" : "Not connected yet"}
            </div>
          </div>
          <Stat label="Google passes" value={h.google_wallet.passes ?? 0} testid="wallet-health-google-passes" />
          <Stat label="Saved on phones" value={h.google_wallet.saved ?? 0} testid="wallet-health-google-saved" />
          <Stat label="Synced 24h" value={h.google_wallet.last_24h?.synced ?? 0} testid="wallet-health-google-synced" />
          <Stat label="Messages 24h" value={h.google_wallet.last_24h?.messages ?? 0} testid="wallet-health-google-messages" />
          <Stat label="Pending" value={h.google_wallet.pending ?? 0} tone={h.google_wallet.pending ? "text-[#C2410C]" : undefined} testid="wallet-health-google-pending" />
          <div className="ml-auto flex flex-col items-end gap-1.5">
            <button data-testid="wallet-health-google-check" onClick={checkGoogle} disabled={gcheck?.busy}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#E4E0D6] bg-white text-[12px] font-medium text-[#0B0D10] hover:border-[#0B0D10]/40 disabled:opacity-50 transition-colors">
              <RefreshCw size={12} className={gcheck?.busy ? "animate-spin" : ""} /> Check connection
            </button>
            {gcheck && !gcheck.busy && (
              <div data-testid="wallet-health-google-check-result" className={`text-[11.5px] max-w-[420px] text-right ${gcheck.ok ? "text-[#3a7d2c]" : "text-[#C2410C]"}`}>{gcheck.message}</div>
            )}
          </div>
        </div>
      )}
      {h.recent_failures?.length > 0 && (
        <ul data-testid="wallet-health-failures" className="mt-4 pt-3 border-t border-[#0B0D10]/[0.06] space-y-1 text-[12px] text-[#3a3d44]">
          {h.recent_failures.slice(0, 5).map((f, i) => (
            <li key={i} className="flex gap-3"><span className="font-mono text-[#3a3d44]/50">{ago(f.at)}</span>
              <span className="font-medium">{f.org || "—"}</span><span className="font-mono">{f.serial}</span>
              <span className="text-[#C2410C] truncate">{f.type === "gave_up" ? "gave up" : `retry #${f.attempt}`} · {f.error}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}
